import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const empty = {
  hero_title: { fr: "", nl: "", en: "" }, hero_subtitle: { fr: "", nl: "", en: "" },
  about_text: { fr: "", nl: "", en: "" }, phone: "", whatsapp: "", telegram: "", email: "",
  address: "", hours: { fr: "", nl: "", en: "" },
  facebook: "", instagram: "", tiktok: "", youtube: "", map_embed: "",
  hero_video_url: "",
  sim_video_url: "", sim_video_poster: "", sim_reel_1: "", sim_reel_2: "", sim_reel_3: "",
  showroom_video_url: "",
  sim_youtube_1: "", sim_youtube_2: "", sim_youtube_3: "",
  sim_reviews: [{ quote:"", name:"", role:"" }, { quote:"", name:"", role:"" }, { quote:"", name:"", role:"" }],
  sim_prices: [{ label:"", price:"", note:"", featured:"" }, { label:"", price:"", note:"", featured:"" }, { label:"", price:"", note:"", featured:"" }],
  services: [],
  sim_hero_title: { fr: "", nl: "", en: "" },
  sim_videos_title: { fr: "", nl: "", en: "" },
  sim_gallery: [],
};

const ICON_CHOICES = ["Car","Handshake","DollarSign","RefreshCw","CreditCard","Truck","Search","Gem","Gauge","Wrench","ShieldCheck","Award","Star","Key","MapPin"];

export default function AdminSite() {
  const [form, setForm] = useState(empty);
  const [uploadingKey, setUploadingKey] = useState("");
  useEffect(() => { api.get("/site").then((r) => setForm({ ...empty, ...r.data, sim_reviews: r.data.sim_reviews?.length ? r.data.sim_reviews : empty.sim_reviews, sim_prices: r.data.sim_prices?.length ? r.data.sim_prices : empty.sim_prices, services: r.data.services || [] })); }, []);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const onUploadVideo = async (key, e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(key, `${API_BASE}/files/${data.id}`);
      toast.success("Vidéo uploadée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload vidéo échoué");
    } finally {
      setUploadingKey("");
      e.target.value = "";
    }
  };
  const setLocalized = (k, lang, v) => setForm((s) => ({ ...s, [k]: { ...(s[k] || {}), [lang]: v } }));
  const setArrItem = (k, idx, field, v) => setForm((s) => {
    const arr = [...(s[k] || [])];
    arr[idx] = { ...arr[idx], [field]: v };
    return { ...s, [k]: arr };
  });

  const save = async () => {
    try { await api.put("/site", form); toast.success("Contenu enregistré"); }
    catch (e) {
      console.error("save site error:", e, e?.response?.data);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : formatApiErrorDetail(detail) || "Enregistrement impossible");
    }
  };

  return (
    <div data-testid="admin-site">
      <h1 className="font-display text-4xl tracking-tight font-light text-white mb-8">Contenu du site</h1>

      <div className="space-y-8">
        <Group title="Coordonnées">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Téléphone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="WhatsApp (chiffres uniquement, ex: 32486521906)"><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="32486521906" className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="Telegram (pseudo sans @)"><Input value={form.telegram} onChange={(e) => set("telegram", e.target.value)} placeholder="kvinternational" className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="Adresse"><Input value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="Facebook URL"><Input value={form.facebook} onChange={(e) => set("facebook", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="Instagram URL"><Input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="TikTok URL"><Input value={form.tiktok} onChange={(e) => set("tiktok", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="YouTube URL"><Input value={form.youtube} onChange={(e) => set("youtube", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
          </div>
          <Field label="Google Maps embed URL"><Input value={form.map_embed} onChange={(e) => set("map_embed", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
        </Group>

        <Group title="Showroom — Vidéo">
          <VideoField label="Vidéo Visite Virtuelle SHOWROOM (URL ou upload — vide = lecteur vide sur /showroom)" fieldKey="showroom_video_url" value={form.showroom_video_url} onChange={(v) => set("showroom_video_url", v)} onUpload={onUploadVideo} uploading={uploadingKey === "showroom_video_url"} />
        </Group>

        <Group title="Simulateur — vidéos">
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-md text-amber-200 text-xs leading-relaxed">
            <strong>Conseil :</strong> pour les vidéos, préférez le bouton <em>« + Uploader vidéo »</em> (stockage direct, streaming fluide, jusqu'à 200 Mo).<br />
            Les liens Google Drive « <em>view?usp=sharing</em> » ne sont pas lisibles en tant que vidéo. Nous convertissons automatiquement au format direct, mais Drive bloque les fichiers &gt; ~100 Mo.<br />
            Pour de longues vidéos : uploadez sur <strong>YouTube</strong> puis collez le lien dans les champs YouTube 1/2/3 ci-dessous (embed natif, aucune limite).
          </div>
          <VideoField label="Vidéo hero page ACCUEIL (background — uploadez ou collez une URL)" fieldKey="hero_video_url" value={form.hero_video_url} onChange={(v) => set("hero_video_url", v)} onUpload={onUploadVideo} uploading={uploadingKey === "hero_video_url"} />
          <VideoField label="Vidéo hero SIMULATEUR (background — uploadez ou collez une URL)" fieldKey="sim_video_url" value={form.sim_video_url} onChange={(v) => set("sim_video_url", v)} onUpload={onUploadVideo} uploading={uploadingKey === "sim_video_url"} />
          <div className="bg-[#0D0D0D]/50 border border-[#C8A75A]/25 rounded-md p-4 mb-4">
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <label className="text-xs uppercase tracking-[0.25em] text-[#C8A75A] font-semibold">Poster vidéo Simulateur (image d'affiche, affichée avant le 1er frame)</label>
              <label className="cursor-pointer text-[10px] uppercase tracking-widest text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black border border-[#C8A75A]/40 rounded-md px-3 py-2 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onUploadVideo("sim_video_poster", e)}
                  disabled={uploadingKey === "sim_video_poster"}
                  className="hidden"
                  data-testid="sim-poster-upload"
                />
                {uploadingKey === "sim_video_poster" ? "Upload…" : "Téléverser une image"}
              </label>
            </div>
            <Input
              data-testid="sim-video-poster-url"
              value={form.sim_video_poster}
              onChange={(e) => set("sim_video_poster", e.target.value)}
              className="bg-[#0D0D0D] border-white/15 focus:border-[#C8A75A]/60 h-11 font-mono text-sm"
              placeholder="Collez l'URL d'une image (jpg, png, webp) ou téléversez"
            />
            <p className="text-white/50 text-[11px] mt-2 leading-relaxed">
              Affichée pendant les premiers ms de chargement de la vidéo — évite le flash noir. Idéalement une frame représentative du hero (1920×1080, &lt; 200 Ko).
            </p>
            {form.sim_video_poster && (
              <div className="mt-3 flex items-center gap-3">
                <img src={form.sim_video_poster} alt="Aperçu poster" className="w-24 h-14 object-cover rounded-md border border-white/10" />
                <a href={form.sim_video_poster} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#C8A75A] text-xs truncate flex-1">{form.sim_video_poster}</a>
                <button type="button" onClick={() => set("sim_video_poster", "")} className="text-white/40 hover:text-red-400 text-[10px] uppercase tracking-widest">Retirer</button>
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <VideoField label="Reel 1" fieldKey="sim_reel_1" value={form.sim_reel_1} onChange={(v) => set("sim_reel_1", v)} onUpload={onUploadVideo} uploading={uploadingKey === "sim_reel_1"} />
            <VideoField label="Reel 2" fieldKey="sim_reel_2" value={form.sim_reel_2} onChange={(v) => set("sim_reel_2", v)} onUpload={onUploadVideo} uploading={uploadingKey === "sim_reel_2"} />
            <VideoField label="Reel 3" fieldKey="sim_reel_3" value={form.sim_reel_3} onChange={(v) => set("sim_reel_3", v)} onUpload={onUploadVideo} uploading={uploadingKey === "sim_reel_3"} />
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-2">
            <Field label="YouTube 1 (URL, ex: https://youtu.be/xxx)"><Input value={form.sim_youtube_1} onChange={(e) => set("sim_youtube_1", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="YouTube 2 (URL)"><Input value={form.sim_youtube_2} onChange={(e) => set("sim_youtube_2", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
            <Field label="YouTube 3 (URL)"><Input value={form.sim_youtube_3} onChange={(e) => set("sim_youtube_3", e.target.value)} className="bg-[#0D0D0D] border-white/10" /></Field>
          </div>
        </Group>

        <Localized title="Titre hero (Accueil)" field="hero_title" form={form} setLocalized={setLocalized} />
        <Localized title="Sous-titre hero (Accueil)" field="hero_subtitle" form={form} setLocalized={setLocalized} textarea />
        <Localized title="Titre hero SIMULATEUR (« Pilotez au plus haut niveau… »)" field="sim_hero_title" form={form} setLocalized={setLocalized} textarea />
        <Localized title="Titre section vidéos SIMULATEUR (« Le simulateur en vidéo »)" field="sim_videos_title" form={form} setLocalized={setLocalized} />
        <Localized title="À propos" field="about_text" form={form} setLocalized={setLocalized} textarea rows={5} />
        <Localized title="Horaires" field="hours" form={form} setLocalized={setLocalized} />

        <Group title="Simulateur — avis de professionnels">
          <p className="text-white/50 text-xs mb-4">Ajoutez de vrais avis de pilotes / pros qui utilisent votre simulateur. Aucune limite — la section masque les entrées vides côté public.</p>
          {(form.sim_reviews || []).map((_r, i) => (
            <div key={i} data-testid={`admin-review-row-${i}`} className="grid md:grid-cols-[1fr_auto_auto_auto] gap-3 border-t border-white/5 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
              <Field label={`Citation #${i + 1}`}>
                <Textarea data-testid={`admin-review-quote-${i}`} rows={3} value={form.sim_reviews?.[i]?.quote || ""} onChange={(e) => setArrItem("sim_reviews", i, "quote", e.target.value)} className="bg-[#0D0D0D] border-white/10" placeholder="Ex : Le simulateur le plus proche d'une vraie piste que j'ai testé." />
              </Field>
              <Field label="Nom">
                <Input data-testid={`admin-review-name-${i}`} value={form.sim_reviews?.[i]?.name || ""} onChange={(e) => setArrItem("sim_reviews", i, "name", e.target.value)} className="bg-[#0D0D0D] border-white/10 w-48" placeholder="Ex : Yannick Latour" />
              </Field>
              <Field label="Rôle / Palmarès">
                <Input data-testid={`admin-review-role-${i}`} value={form.sim_reviews?.[i]?.role || ""} onChange={(e) => setArrItem("sim_reviews", i, "role", e.target.value)} className="bg-[#0D0D0D] border-white/10 w-56" placeholder="Ex : Pilote Porsche Cup" />
              </Field>
              <Field label="&nbsp;">
                <Button data-testid={`admin-review-remove-${i}`} type="button" variant="ghost" onClick={() => setForm((s) => ({ ...s, sim_reviews: (s.sim_reviews||[]).filter((_, x) => x !== i) }))} className="h-11 text-red-400 hover:bg-red-400/10 hover:text-red-300 uppercase text-[10px] tracking-widest">Supprimer</Button>
              </Field>
            </div>
          ))}
          <Button data-testid="admin-review-add" type="button" variant="outline" onClick={() => setForm((s) => ({ ...s, sim_reviews: [...(s.sim_reviews||[]), { quote: "", name: "", role: "" }] }))} className="mt-4 border-[#C8A75A] text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black uppercase text-xs tracking-widest">+ Ajouter un avis de pro</Button>
        </Group>

        <Group title="Simulateur — grille tarifaire">
          <p className="text-white/50 text-xs mb-4">Trois formules affichées. Cochez « Populaire » pour mettre une carte en vedette (or).</p>
          {[0, 1, 2].map((i) => (
            <div key={i} className="grid md:grid-cols-[1fr_1fr_2fr_auto] gap-3 border-t border-white/5 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
              <Field label={`Formule #${i + 1}`}>
                <Input data-testid={`admin-price-label-${i}`} value={form.sim_prices?.[i]?.label || ""} onChange={(e) => setArrItem("sim_prices", i, "label", e.target.value)} className="bg-[#0D0D0D] border-white/10" placeholder="ex: Découverte" />
              </Field>
              <Field label="Prix">
                <Input data-testid={`admin-price-price-${i}`} value={form.sim_prices?.[i]?.price || ""} onChange={(e) => setArrItem("sim_prices", i, "price", e.target.value)} className="bg-[#0D0D0D] border-white/10" placeholder="ex: 89 €" />
              </Field>
              <Field label="Description courte">
                <Input data-testid={`admin-price-note-${i}`} value={form.sim_prices?.[i]?.note || ""} onChange={(e) => setArrItem("sim_prices", i, "note", e.target.value)} className="bg-[#0D0D0D] border-white/10" placeholder="ex: 1 session 60 min · Briefing + debrief" />
              </Field>
              <Field label="Populaire">
                <label className="flex items-center gap-2 h-11 px-3 border border-white/10 rounded-md bg-[#0D0D0D]">
                  <input
                    type="checkbox"
                    data-testid={`admin-price-featured-${i}`}
                    checked={form.sim_prices?.[i]?.featured === "1"}
                    onChange={(e) => setArrItem("sim_prices", i, "featured", e.target.checked ? "1" : "")}
                    className="w-4 h-4 accent-[#C8A75A]"
                  />
                  <span className="text-xs text-white/70">Vedette</span>
                </label>
              </Field>
            </div>
          ))}
        </Group>

        <Group title="Services (Nos services) — ajoutez, modifiez, supprimez à votre guise">
          <p className="text-white/50 text-xs mb-4">Cartes affichées sur la page /services. Icônes issues de lucide-react (ex: Car, Handshake, DollarSign, Gauge, Wrench, ShieldCheck…).</p>
          {(form.services || []).map((svc, i) => (
            <div key={i} data-testid={`admin-service-row-${i}`} className="border-t border-white/5 pt-4 mt-4 first:border-0 first:pt-0 first:mt-0">
              <div className="grid md:grid-cols-[220px_1fr_auto] gap-3">
                <Field label={`Icône #${i + 1}`}>
                  <select
                    data-testid={`admin-service-icon-${i}`}
                    value={svc.icon || "Gem"}
                    onChange={(e) => setForm((s) => { const arr = [...(s.services||[])]; arr[i] = { ...arr[i], icon: e.target.value }; return { ...s, services: arr }; })}
                    className="w-full h-11 bg-[#0D0D0D] border border-white/10 rounded-md text-white text-sm px-3"
                  >
                    {ICON_CHOICES.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </Field>
                <Field label="Titre — FR / NL / EN">
                  <div className="grid grid-cols-3 gap-2">
                    {["fr","nl","en"].map((l) => (
                      <Input
                        key={l}
                        data-testid={`admin-service-title-${i}-${l}`}
                        placeholder={l.toUpperCase()}
                        value={svc.title?.[l] || ""}
                        onChange={(e) => setForm((s) => { const arr = [...(s.services||[])]; arr[i] = { ...arr[i], title: { ...(arr[i].title||{}), [l]: e.target.value } }; return { ...s, services: arr }; })}
                        className="bg-[#0D0D0D] border-white/10"
                      />
                    ))}
                  </div>
                </Field>
                <Field label="&nbsp;">
                  <Button
                    data-testid={`admin-service-remove-${i}`}
                    type="button"
                    variant="ghost"
                    onClick={() => setForm((s) => ({ ...s, services: (s.services||[]).filter((_, x) => x !== i) }))}
                    className="h-11 text-red-400 hover:bg-red-400/10 hover:text-red-300 uppercase text-xs tracking-widest"
                  >
                    Supprimer
                  </Button>
                </Field>
              </div>
              <Field label="Description — FR / NL / EN">
                <div className="grid grid-cols-3 gap-2">
                  {["fr","nl","en"].map((l) => (
                    <Textarea
                      key={l}
                      rows={2}
                      data-testid={`admin-service-text-${i}-${l}`}
                      placeholder={l.toUpperCase()}
                      value={svc.text?.[l] || ""}
                      onChange={(e) => setForm((s) => { const arr = [...(s.services||[])]; arr[i] = { ...arr[i], text: { ...(arr[i].text||{}), [l]: e.target.value } }; return { ...s, services: arr }; })}
                      className="bg-[#0D0D0D] border-white/10"
                    />
                  ))}
                </div>
              </Field>
            </div>
          ))}
          <Button
            data-testid="admin-service-add"
            type="button"
            variant="outline"
            onClick={() => setForm((s) => ({ ...s, services: [ ...(s.services||[]), { icon: "Gem", title: { fr:"", nl:"", en:"" }, text: { fr:"", nl:"", en:"" } } ] }))}
            className="mt-4 border-[#C8A75A] text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black uppercase text-xs tracking-widest"
          >
            + Ajouter un service
          </Button>
        </Group>

        <Group title="Simulateur — Galerie « Ils ont pris le volant »">
          <p className="text-white/50 text-xs mb-4">Uploadez ou collez l'URL de vos vraies photos de sessions. Vide = section masquée sur /simulator.</p>
          {(form.sim_gallery || []).map((g, i) => (
            <div key={i} data-testid={`admin-gallery-row-${i}`} className="grid md:grid-cols-[120px_1fr_auto] gap-3 border-t border-white/5 pt-3 mt-3 first:border-0 first:pt-0 first:mt-0">
              <div className="aspect-square bg-[#0D0D0D] border border-white/10 rounded-md overflow-hidden">
                {g.img && <img src={g.img} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="space-y-2">
                <Input placeholder="URL de la photo" data-testid={`admin-gallery-img-${i}`} value={g.img || ""} onChange={(e) => setForm((s) => { const arr = [...(s.sim_gallery||[])]; arr[i] = { ...arr[i], img: e.target.value }; return { ...s, sim_gallery: arr }; })} className="bg-[#0D0D0D] border-white/10" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Titre (ex: Session 24h Spa)" data-testid={`admin-gallery-title-${i}`} value={g.title || ""} onChange={(e) => setForm((s) => { const arr = [...(s.sim_gallery||[])]; arr[i] = { ...arr[i], title: e.target.value }; return { ...s, sim_gallery: arr }; })} className="bg-[#0D0D0D] border-white/10" />
                  <Input placeholder="Note (ex: Circuit · durée)" data-testid={`admin-gallery-note-${i}`} value={g.note || ""} onChange={(e) => setForm((s) => { const arr = [...(s.sim_gallery||[])]; arr[i] = { ...arr[i], note: e.target.value }; return { ...s, sim_gallery: arr }; })} className="bg-[#0D0D0D] border-white/10" />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="cursor-pointer text-[10px] uppercase tracking-widest text-[#C8A75A] hover:text-white border border-[#C8A75A]/40 rounded-md px-3 py-2 text-center">
                  <input type="file" accept="image/*" className="hidden"
                    onChange={async (e) => {
                      const f = (e.target.files||[])[0]; if (!f) return;
                      const fd = new FormData(); fd.append("file", f);
                      try {
                        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
                        setForm((s) => { const arr = [...(s.sim_gallery||[])]; arr[i] = { ...arr[i], img: `${API_BASE}/files/${data.id}` }; return { ...s, sim_gallery: arr }; });
                        toast.success("Photo uploadée");
                      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload échoué"); }
                      e.target.value = "";
                    }}
                  />+ Photo
                </label>
                <Button data-testid={`admin-gallery-remove-${i}`} type="button" variant="ghost" onClick={() => setForm((s) => ({ ...s, sim_gallery: (s.sim_gallery||[]).filter((_, x) => x !== i) }))} className="text-red-400 hover:bg-red-400/10 hover:text-red-300 uppercase text-[10px] tracking-widest">Supprimer</Button>
              </div>
            </div>
          ))}
          <Button data-testid="admin-gallery-add" type="button" variant="outline" onClick={() => setForm((s) => ({ ...s, sim_gallery: [...(s.sim_gallery||[]), { img: "", title: "", note: "" }] }))} className="mt-4 border-[#C8A75A] text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black uppercase text-xs tracking-widest">+ Ajouter une photo galerie</Button>
        </Group>

        <div className="pt-6 border-t border-white/10">
          <Button data-testid="save-site" onClick={save} className="bg-[#C8A75A] text-black hover:bg-white uppercase text-xs tracking-widest px-8">Enregistrer</Button>
        </div>
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="bg-[#1E1E1E] border border-white/10 rounded-md p-6">
      <h3 className="font-display text-2xl text-white mb-6">{title}</h3>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div className="mt-3">
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">{label}</label>
      {children}
    </div>
  );
}
function VideoField({ label, fieldKey, value, onChange, onUpload, uploading }) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2 gap-3">
        <label className="text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</label>
        <label className={`text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-[#C8A75A]/40 text-[#C8A75A] hover:bg-[#C8A75A] hover:text-black cursor-pointer transition rounded ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
          <input
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            onChange={(e) => onUpload(fieldKey, e)}
            disabled={uploading}
            className="hidden"
            data-testid={`site-video-upload-${fieldKey}`}
          />
          {uploading ? "Upload…" : "+ Uploader vidéo"}
        </label>
      </div>
      <Input data-testid={`site-video-url-${fieldKey}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-[#0D0D0D] border-white/10" placeholder="URL vidéo (ou uploadez ci-dessus)" />
      {value ? <p className="text-[10px] text-white/40 mt-1 truncate">✓ {value}</p> : null}
    </div>
  );
}
function Localized({ title, field, form, setLocalized, textarea, rows = 3 }) {
  const V = textarea ? Textarea : Input;
  const val = form[field] || { fr: "", nl: "", en: "" };
  return (
    <Group title={title}>
      <div className="grid md:grid-cols-3 gap-4">
        {["fr", "nl", "en"].map((l) => (
          <Field key={l} label={l.toUpperCase()}>
            <V rows={rows} value={val[l] || ""} onChange={(e) => setLocalized(field, l, e.target.value)} className="bg-[#0D0D0D] border-white/10" />
          </Field>
        ))}
      </div>
    </Group>
  );
}
